import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import TerminalWindow from '../components/TerminalWindow';
import { useTheme } from '../contexts/ThemeContext';

const asciiLogo = [
  "         _          ",
  "        / /\\        ",
  "       / /  \\       ",
  "      / / /\\ \\      ",
  "     / / /\\ \\ \\     ",
  "    / / /  \\ \\ \\    ",
  "   / / /___/ /\\ \\   ",
  "  / / /_____/ /\\ \\  ",
  " / /_________/\\ \\ ",
  " / / /_       __\\ \\_\\",
  " \\_\\___\\     /____/_/",
  "                    "
];

const profile = {
  name: 'Alaa',
  title: 'AI Engineer & Linux Addict',
  location: 'Earth',
  os: 'Arch Linux',
  shell: 'zsh',
  editor: 'Neovim',
  languages: 'Python, Rust',
  interests: 'AI, Linux',
  website: 'alaa.dev',
  publication: [
    {
      title: 'Enhancing Learning Recommendations in MOOC Search Engines Through Named Entity Recognition',
      status: 'Published in 2024. Read online:',
      link: 'https://link.springer.com/article/10.1007/s10639-024-13308-4',
      description: [
        'Contributed novel methods in Named Entity Recognition (NER) to improve the accuracy and relevance of recommendations in Massive Open Online Courses (MOOC) platforms, addressing challenges in course classification and recommendation precision.',
        'Developed an automated method to create NER datasets for any textual data, significantly reducing the time and effort involved in dataset creation for training models in NER tasks.'
      ]
    }
  ],
  projects: [
    {
      name: 'AI-powered Search Engine for E-learning (Master\'s Graduation Project)',
      desc: 'Developed an advanced modular search engine using ML and NLP, including NER for query understanding, Siamese Networks with LSTM for semantic matching, query expansion with LSTM and GNN, and attention mechanisms for improved recommendations.',
      tech: ['Python', 'NLP', 'Neural Networks', 'LSTM', 'GNN', 'Attention']
    },
    {
      name: 'Knowledge Base with RAG Pipeline',
      desc: 'Worked on a knowledge base system with RAG (Retrieval-Augmented Generation) pipeline where code editors connect to the knowledge base using MCP (Model Context Protocol) with an agentic chatbot to perform actions and search the knowledge base.',
      tech: ['RAG', 'MCP', 'AI Agents', 'Knowledge Base', 'Python']
    },
    {
      name: 'Distributed File Management System',
      desc: 'File management system using RPC and peer-to-peer architecture, enabling seamless file sharing and local-like storage experience.',
      tech: ['Python', 'P2P', 'RPC']
    },
    {
      name: 'Cognorust',
      desc: 'A machine learning library I am building from scratch in Rust. Cognorust starts with tensor operations and aims to provide a full suite of ML algorithms. It is still a work in progress and under active development. Built entirely in Rust for performance and safety.',
      tech: ['Rust', 'Tensors', 'Machine Learning', 'WIP']
    }
  ],
  skills: [
    'AI Agents',
    'Machine Learning',
    'Deep Learning (TensorFlow, Keras)',
    'PyTorch',
    'Neural Networks',
    'NLP (Transformers, BERT)',
    'Predictive Modeling',
    'Data Preprocessing (Pandas, NumPy, Scikit-learn)',
    'Python',
    'Bash',
    'Rust',
    'Django',
    'REST APIs',
    'PostgreSQL',
    'Redis',
    'Git',
    'Linux (Arch)',
    'Linux command-line',
    'Web Scraping',
    'Scrapy',
    'AI Agent Design',
  ],
  tools: [
    'Python',
    'Rust',
    'Bash',
    'Docker',
    'Git',
    'Linux',
    'Vim/Neovim',
    'tmux',
    'FastAPI',
    'Django',
    'PyTorch',
    'TensorFlow',
    'HuggingFace',
    'Jupyter',
    'zsh',
    'Scrapy',
    'LangGraph',
    'CrewAI',
  ],
};

const getLanguageIcon = (lang) => {
  const icons = {
    'Python': '🐍',
    'Rust': '🦀',
    'Bash': '💻',
    'Docker': '🐳',
    'Git': '📦',
    'Linux': '🐧',
    'Vim/Neovim': '⚡',
    'tmux': '🖥️',
    'FastAPI': '🚀',
    'Django': '🎸',
    'PyTorch': '🔥',
    'TensorFlow': '🧠',
    'HuggingFace': '🤗',
    'Jupyter': '📊',
    'zsh': '⚡',
    'P2P': '🌐',
    'Network': '🔗',
    'IoT': '📡',
    'Analytics': '📈',
    'Neural Networks': '🧠',
    'NLP': '💬',
    'AutoML': '🤖',
    'ML': '📊',
    'Performance': '⚡',
    'LangGraph': '🕸️',
    'CrewAI': '👥',
    'Scrapy': '🕷️',
    'RAG': '🔍',
    'MCP': '🔌',
    'AI Agents': '🤖',
    'Knowledge Base': '📚',
  };
  return icons[lang] || '🔧';
};

const asciiLogoLines = asciiLogo;

const infoLines = [
  'abdelhadi@about',
  `<span class=\"text-[#39c5bb]\">OS:</span> <span class=\"text-[#e5e5e5]\">${profile.os}</span>`,
  `<span class=\"text-[#39c5bb]\">Title:</span> <span class=\"text-[#e5e5e5]\">${profile.title}</span>`,
  `<span class=\"text-[#39c5bb]\">Location:</span> <span class=\"text-[#e5e5e5]\">${profile.location}</span>`,
  `<span class=\"text-[#39c5bb]\">Shell:</span> <span class=\"text-[#e5e5e5]\">${profile.shell}</span>`,
  `<span class=\"text-[#39c5bb]\">Editor:</span> <span class=\"text-[#e5e5e5]\">${profile.editor}</span>`,
  `<span class=\"text-[#39c5bb]\">Languages:</span> <span class=\"text-[#e5e5e5]\">${profile.languages}</span>`,
  `<span class=\"text-[#39c5bb]\">Interests:</span> <span class=\"text-[#e5e5e5]\">${profile.interests}</span>`
];

// Helper for info line style
const infoLineStyle = {
  color: '#e0e0e0',
  fontWeight: 600,
};

function About() {
  // Typing animation for the command
  const command = 'neofetch';
  const [typedCommand, setTypedCommand] = useState('');
  const [step, setStep] = useState(0); // 0: typing, 1: waiting, 2+: reveal lines
  // Add a state to detect mobile (using window width)
  const [isMobile, setIsMobile] = useState(false);
  // Featured posts state
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [postsError, setPostsError] = useState(null);
  // Theme context
  const { currentTheme } = useTheme();

  // Animate each info+logo line
  useEffect(() => {
    if (step === 0 && typedCommand.length < command.length) {
      const timeout = setTimeout(() => {
        setTypedCommand(command.slice(0, typedCommand.length + 1));
      }, 80);
      return () => clearTimeout(timeout);
    } else if (step === 0 && typedCommand.length === command.length) {
      // After typing, wait, then start revealing lines
      const timeout = setTimeout(() => setStep(1), 400);
      return () => clearTimeout(timeout);
    } else if (step > 0 && step <= asciiLogoLines.length) {
      // Reveal each line one by one
      const timeout = setTimeout(() => setStep(step + 1), 180);
      return () => clearTimeout(timeout);
    }
  }, [typedCommand, step]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        setLoadingPosts(true);
        setPostsError(null);
        const response = await api.get('/posts/?page=1&page_size=3');
        let postsData = [];
        if (Array.isArray(response.data)) {
          postsData = response.data;
        } else if (response.data && Array.isArray(response.data.results)) {
          postsData = response.data.results;
        } else if (response.data && typeof response.data === 'object') {
          postsData = [response.data];
        }
        setFeaturedPosts(postsData);
      } catch (err) {
        setPostsError('Failed to load featured posts');
        setFeaturedPosts([]);
      } finally {
        setLoadingPosts(false);
      }
    };
    fetchFeatured();
  }, []);

  // Set a custom terminal-themed browser tab title and favicon
  useEffect(() => {
    document.title = 'abdelhadi@about:~$ neofetch';
    // Set favicon to logo.png
    const favicon = document.querySelector("link[rel='icon']") || document.createElement('link');
    favicon.rel = 'icon';
    favicon.type = 'image/png';
    favicon.href = '/logo.png';
    document.head.appendChild(favicon);
  }, []);

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-start py-8 sm:py-16 px-2 sm:px-4 md:px-8 bg-[#181a1b]" dir="ltr">
      <TerminalWindow
        prompt="abdelhadi@about:~$"
        promptAddon={
          step === 0 ? (
            <>
              <span className="text-[#00ff66] ml-2">{typedCommand}</span>
              <span className="terminal-cursor ml-1" style={{ opacity: typedCommand.length === command.length ? 0 : 1 }}>|</span>
            </>
          ) : (
            <span className="text-[#00ff66] ml-2">neofetch</span>
          )
        }
        className="w-full max-w-4xl"
      >
        {/* Terminal content */}
        <div className="flex flex-col gap-8 font-mono animate-fadein text-left overflow-x-auto">
          {/* Neofetch-style output */}
          {step > 0 && (
            isMobile ? (
              <div className="neofetch-mobileblock w-full text-sm leading-tight select-none" style={{ fontFamily: 'monospace' }}>
                {/* Logo block */}
                <div className="flex flex-col mb-2" style={{ paddingLeft: '3.5em' }}>
                  {asciiLogoLines.map((logoLine, i) => (
                    <span
                      key={i}
                      className="text-[#00ff66] neofetch-logo-line"
                      style={{
                        display: 'inline-block',
                        whiteSpace: 'pre',
                        fontSize: '0.95em',
                        verticalAlign: 'top',
                      }}
                    >
                      {step > i ? logoLine : step === i ? logoLine : ''}
                    </span>
                  ))}
                </div>
                {/* Info block */}
                <div className="flex flex-col items-start gap-0.5" style={{ paddingLeft: '3.5em' }}>
                  {(() => {
                    const logoLines = asciiLogoLines.length;
                    const infoLinesArr = infoLines;
                    const infoLinesCount = infoLinesArr.length;
                    const offset = Math.max(0, Math.floor((logoLines - infoLinesCount) / 2));
                    let infoIdx = 0;
                    return Array.from({ length: logoLines }).map((_, i) => {
                      if (i < offset || infoIdx >= infoLinesCount) {
                        return null;
                      } else {
                        let content = step > i ? infoLinesArr[infoIdx] : step === i ? '<span class="terminal-cursor">|</span>' : '';
                        if (step > i && infoIdx === 8) {
                          content = `<span class=\"text-[#39c5bb]\">Website:</span> <a href=\"https://${profile.website}\" class=\"underline text-[#00ff66] truncate inline-block max-w-full\" style=\"text-overflow:ellipsis;overflow:hidden;white-space:nowrap;vertical-align:bottom;\">${profile.website}</a>`;
                        }
                        infoIdx++;
                        return (
                          <span
                            key={i}
                            className="neofetch-info-line"
                            style={{
                              fontSize: '1em',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-all',
                              marginBottom: '0.35em',
                              ...infoLineStyle,
                            }}
                          >
                            <span dangerouslySetInnerHTML={{ __html: content }} />
                          </span>
                        );
                      }
                    });
                  })()}
                </div>
              </div>
            ) : (
              <div
                className="neofetch-rowblock w-full text-sm sm:text-base leading-tight select-none"
                style={{ fontFamily: 'monospace' }}
              >
                {(() => {
                  const logoLines = asciiLogoLines.length;
                  const infoLinesArr = infoLines;
                  const infoLinesCount = infoLinesArr.length;
                  const offset = Math.max(0, Math.floor((logoLines - infoLinesCount) / 2));
                  const totalLines = logoLines;
                  let infoIdx = 0;
                  return Array.from({ length: totalLines }).map((_, i) => {
                    // Left: logo line
                    const logoLine = step > i ? asciiLogoLines[i] : step === i ? asciiLogoLines[i] : '';
                    // Right: info line (with offset)
                    let infoContent = '';
                    if (i >= offset && infoIdx < infoLinesCount) {
                      if (step > i) {
                        if (infoIdx === 8) {
                          infoContent = `<span class=\"text-[#39c5bb]\">Website:</span> <a href=\"https://${profile.website}\" class=\"underline text-[#00ff66] truncate inline-block max-w-full\" style=\"text-overflow:ellipsis;overflow:hidden;white-space:nowrap;vertical-align:bottom;\">${profile.website}</a>`;
                        } else {
                          infoContent = infoLinesArr[infoIdx];
                        }
                      } else if (step === i) {
                        infoContent = '<span class=\"terminal-cursor\">|</span>';
                      }
                      infoIdx++;
                    }
                    return (
                      <div key={i} className="flex flex-row w-full min-w-0" style={{ minHeight: '1.2em' }}>
                        <span
                          className="text-[#00ff66] neofetch-logo-line"
                          style={{
                            display: 'inline-block',
                            width: '7em',
                            minWidth: '7em',
                            maxWidth: '7em',
                            whiteSpace: 'pre',
                            fontSize: isMobile ? '0.95em' : undefined,
                            verticalAlign: 'top',
                          }}
                        >
                          {logoLine}
                        </span>
                        <span
                          className="neofetch-info-line"
                          style={{
                            display: 'inline-block',
                            verticalAlign: 'top',
                            fontSize: isMobile ? '0.95em' : undefined,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                            maxWidth: '100%',
                            minWidth: 0,
                            marginLeft: '7em',
                            ...infoLineStyle,
                          }}
                        >
                          <span dangerouslySetInnerHTML={{ __html: infoContent }} />
                        </span>
                      </div>
                    );
                  });
                })()}
              </div>
            )
          )}
          {/* Special message for lolo theme */}
          {step > asciiLogoLines.length && currentTheme === 'lolo' && (
            <div className="mt-8 animate-fadein">
              <div className="bg-[#2a1538] border-2 border-[#d946ef] rounded-xl p-6 text-center">
                <div className="text-[#d946ef] text-2xl font-bold mb-2">💜</div>
                <div className="text-[#e9d5ff] text-xl font-bold leading-relaxed">
                  The biggest thing I am proud of is being your man
                </div>
                <div className="text-[#d946ef] text-2xl font-bold mt-2">💜</div>
              </div>
            </div>
          )}
          {/* Publication */}
          {step > asciiLogoLines.length && (
            <div className="mt-8 animate-fadein">
              <div className="text-[#00ff66] font-bold mb-2"># Publication</div>
              <ul className="pl-4 flex flex-col gap-2">
                {profile.publication.map((pub, i) => (
                  <li key={i} className="text-[#e5e5e5]">
                    <span className="text-[#39c5bb]">{pub.title}</span>
                    <div className="text-xs text-[#00ff66] mb-1">{pub.status} <a href={pub.link} target="_blank" rel="noopener noreferrer" className="underline text-[#00ff66]">{pub.link}</a></div>
                    <div className="flex flex-col gap-2 mt-1">
                      {pub.description.map((desc, j) => (
                        <div key={j} className="flex items-start gap-2">
                          <span className="text-[#00ff66] mt-0.5">•</span>
                          <span className="text-[#bdbdbd] text-sm">{desc}</span>
                        </div>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* Extra sections: Projects, Skills, Tools */}
          {step > asciiLogoLines.length && (
            <>
              {/* Projects */}
              <div className="mt-8 animate-fadein">
                <div className="text-[#00ff66] font-bold mb-2"># Projects</div>
                <ul className="pl-4 flex flex-col gap-2">
                  {profile.projects.map((proj) => (
                    <li key={proj.name} className="text-[#e5e5e5]">
                      <span className="text-[#39c5bb]">{proj.name}:</span>
                      <span className="ml-2 text-[#bdbdbd]">{proj.desc}</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {proj.tech.map((tech) => (
                          <span key={tech} className="bg-[#181a1b] px-2 py-1 rounded text-xs border border-[#39c5bb] whitespace-nowrap">
                            {getLanguageIcon(tech)} {tech}
                          </span>
                        ))}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              {/* Skills */}
              <div className="mt-8 animate-fadein">
                <div className="text-[#00ff66] font-bold mb-2"># Skills</div>
                <ul className="pl-4 flex flex-wrap gap-2">
                  {profile.skills.map((skill) => (
                    <li key={skill} className="bg-[#181a1b] px-3 py-1 rounded-xl text-[#e5e5e5] border border-[#39c5bb] text-sm">{skill}</li>
                  ))}
                </ul>
              </div>
              {/* Tools */}
              <div className="mt-8 animate-fadein">
                <div className="text-[#00ff66] font-bold mb-2"># Tools I Know</div>
                <ul className="pl-4 flex flex-wrap gap-2">
                  {profile.tools.map((tool) => (
                    <li key={tool} className="bg-[#23272e] px-3 py-1 rounded-xl text-[#39c5bb] border border-[#00ff66] text-sm">
                      {getLanguageIcon(tool)} {tool}
                    </li>
                  ))}
                </ul>
              </div>
              {/* Featured Posts */}
              <div className="mt-12 animate-fadein">
                <div className="text-[#00ff66] font-bold mb-2 text-lg"># Featured Posts</div>
                {loadingPosts ? (
                  <div className="text-[#39c5bb] text-sm flex items-center gap-2"><span className="animate-spin">⏳</span> Loading...</div>
                ) : postsError ? (
                  <div className="text-[#ff5555] text-sm">{postsError}</div>
                ) : (
                  <div className="flex flex-col gap-4 mt-4 w-full">
                    {featuredPosts.slice(0, 3).map((post) => (
                      <a
                        key={post.slug}
                        href={`/posts/${post.slug}`}
                        className="w-full bg-[#181a1b] border border-[#39c5bb] rounded-xl p-4 flex flex-col gap-2 hover:bg-[#23272e] hover:border-[#00ff66] transition-all duration-200 shadow-lg group"
                      >
                        <div className="flex items-center gap-2 text-xs text-[#39c5bb]">
                          <span className="terminal-prompt text-[#39c5bb]">abdelhadi@blog:~$</span>
                          <span className="text-[#00ff66]">stat</span>
                        </div>
                        <div className="text-base font-bold text-[#e5e5e5] group-hover:text-[#00ff66]">{post.title}</div>
                        <div className="text-xs text-[#39c5bb]">{post.published_at || post.date}</div>
                        <div className="text-sm text-[#bdbdbd] line-clamp-3">{post.excerpt}</div>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </TerminalWindow>
      {/* Subtle background pattern */}
      <div 
        className="pointer-events-none fixed inset-0 z-0 opacity-10" 
        style={{
          background: currentTheme === 'lolo' 
            ? 'radial-gradient(circle at 60% 40%, #c084fc 0%, transparent 60%)' 
            : 'radial-gradient(circle at 60% 40%, #00ff66 0%, transparent 60%)'
        }} 
      />
      <style>{`
        @keyframes fadein { from { opacity: 0; transform: translateY(16px);} to { opacity: 1; transform: none; } }
        .animate-fadein { animation: fadein 0.7s cubic-bezier(.4,1.7,.6,.97) both; }
        .terminal-cursor { display: inline-block; width: 1ch; animation: blink 1s steps(1) infinite; }
        @keyframes blink { 0%, 50% { opacity: 1; } 51%, 100% { opacity: 0; } }
        /* Responsive tweaks for neofetch info */
        .neofetch-rowblock {
          width: 100%;
          overflow-x: auto !important;
          overflow-y: hidden !important;
        }
        .neofetch-logo-line {
          width: 7em !important;
          min-width: 7em !important;
          max-width: 7em !important;
          flex-shrink: 0 !important;
          padding: 0 !important;
          margin: 0 !important;
        }
        .neofetch-info-line {
          white-space: pre-wrap !important;
          word-break: break-all !important;
          min-width: 0 !important;
        }
        @media (max-width: 400px) {
          .neofetch-logo-line {
            padding: 0 !important;
            margin: 0 !important;
          }
        }
        @media (max-width: 640px) {
          .neofetch-logo-line {
            padding: 0 !important;
            margin: 0 !important;
          }
        }
      `}</style>
    </div>
  );
}

export default About; 